import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import './DatePicker.scss';

const DAYS_SK = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];
const MONTHS_SK = [
  'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
  'Júl', 'August', 'September', 'Október', 'November', 'December',
];

function DatePicker({
  label,
  value,
  onChange,
  placeholder = 'Vyber dátum...',
  disabled = false,
  minDate,
  maxDate,
  error,
  required = false,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  });
  const [dropdownStyle, setDropdownStyle] = useState({});

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Synchronize viewDate when value prop changes
  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) setViewDate(d);
    }
  }, [value]);

  // Reposition dropdown on resize / scroll
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const dropdownWidth = 250;
      const dropdownHeight = 270;

      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      let top = showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4;
      let left = rect.left;

      if (left + dropdownWidth > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - dropdownWidth - 12);
      }
      if (left < 12) left = 12;

      setDropdownStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
        width: `${dropdownWidth}px`,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      const inInput = inputRef.current && inputRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inInput && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const days = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = (firstDay.getDay() + 6) % 7;
    const result = [];

    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      result.push({ date: d, outside: true });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      result.push({ date: new Date(year, month, i), outside: false });
    }
    const remaining = 42 - result.length;
    for (let i = 1; i <= remaining; i++) {
      result.push({ date: new Date(year, month + 1, i), outside: true });
    }
    return result;
  }, [year, month]);

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!value) return false;
    const d = new Date(value);
    if (isNaN(d.getTime())) return false;
    return date.toDateString() === d.toDateString();
  };

  const isDisabled = (date) => {
    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;
    return false;
  };

  const handleSelect = (date) => {
    if (isDisabled(date)) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const formattedIso = `${y}-${m}-${d}`;

    const eventObj = {
      target: { value: formattedIso },
      value: formattedIso,
      date: date,
      toString: () => formattedIso,
    };

    onChange?.(formattedIso, eventObj);
    setIsOpen(false);
  };

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const handleToday = () => {
    const today = new Date();
    setViewDate(today);
    handleSelect(today);
  };

  const handleClear = () => {
    onChange?.('', { target: { value: '' }, date: null, value: '' });
    setIsOpen(false);
  };

  const formatDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const mon = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${mon}.${d.getFullYear()}`;
  };

  const dropdownJSX = isOpen ? (
    <div
      className="datepicker__dropdown datepicker__dropdown--open"
      style={dropdownStyle}
      ref={dropdownRef}
    >
      <div className="datepicker__header">
        <button type="button" className="datepicker__nav-btn" onClick={handlePrevMonth} aria-label="Predchádzajúci mesiac">
          <ChevronLeft size={16} />
        </button>
        <span className="datepicker__month-year">{MONTHS_SK[month]} {year}</span>
        <button type="button" className="datepicker__nav-btn" onClick={handleNextMonth} aria-label="Nasledujúci mesiac">
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="datepicker__weekdays">
        {DAYS_SK.map((d) => (
          <span key={d} className="datepicker__weekday">{d}</span>
        ))}
      </div>

      <div className="datepicker__days">
        {days.map(({ date, outside }, i) => (
          <button
            key={i}
            type="button"
            className={`datepicker__day ${outside ? 'datepicker__day--outside' : ''} ${isToday(date) ? 'datepicker__day--today' : ''
              } ${isSelected(date) ? 'datepicker__day--selected' : ''} ${isDisabled(date) ? 'datepicker__day--disabled' : ''
              }`}
            onClick={() => !outside && handleSelect(date)}
            disabled={outside || isDisabled(date)}
          >
            {date.getDate()}
          </button>
        ))}
      </div>

      <div className="datepicker__footer">
        <button type="button" className="datepicker__today-btn" onClick={handleToday}>Dnes</button>
        <button type="button" className="datepicker__clear-btn" onClick={handleClear}>Vymazať</button>
      </div>
    </div>
  ) : null;

  return (
    <div className={`datepicker ${className}`} ref={inputRef}>
      {label && <label className="datepicker__label">{label}</label>}

      <div className="datepicker__input-wrapper">
        <input
          type="text"
          className={`datepicker__input ${isOpen ? 'datepicker__input--open' : ''} ${disabled ? 'datepicker__input--disabled' : ''
            } ${error ? 'datepicker__input--error' : ''}`}
          value={value ? formatDate(value) : ''}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          required={required}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
        <Calendar className="datepicker__icon" size={18} />
      </div>

      {dropdownJSX && createPortal(dropdownJSX, document.body)}

      {error && <span className="datepicker__error">{error}</span>}
    </div>
  );
}

export default DatePicker;
